import { datadogRum, } from '@datadog/browser-rum-slim';
import { CUSTOM_EVENT_DATA_FULLY_LOADED_TIMEOUT, DATA_PROP_NAME_VIEW_DATA_FULLY_LOADED, DATA_PROP_NAME_VIEW_INITIAL_RENDER, DATA_PROP_NAME_VIEW_IR_AND_DFL_DELTA, LISTENER_CLASS_FEATURE_LOAD_TIMING, MARK_NAME_VIEW_CHANGED, MARK_NAME_VIEW_DATA_FULLY_LOADED, MARK_NAME_VIEW_INITIAL_RENDER, } from '../constants';
import { clearMarksMadeListenerByClass, getAllMarks, onMarkAdded, whenAllMarksMade, } from '../markers';
import { isExternalFunctionalDep, isUrlAServerlessFnCall, isUrlInternal, isUrlSameOrigin, performanceResourceEntryToContextProps, } from './utils';
const viewChangeTimings = [];
// for live debugging in the browser
// @ts-expect-error
window._rum = window._rum || {};
// @ts-expect-error
window._rum.timings = [];
/**
 * Given a start time, walk back in our list of view changes to find the
 * feature name that was active at that time
 */
function getViewWhenEventHappened(startTime) {
    if (viewChangeTimings.length === 0) {
        return;
    }
    else if (viewChangeTimings.length === 1) {
        return viewChangeTimings[0].featureName;
    }
    const matchedTiming = viewChangeTimings.reduceRight((selectedChangeTiming, timingToCheck, index) => {
        if (!selectedChangeTiming && index === 0) {
            // associate with first view if we are on the last entry
            selectedChangeTiming = timingToCheck;
        }
        if (!selectedChangeTiming && startTime > timingToCheck.viewStartTime) {
            selectedChangeTiming = timingToCheck;
        }
        return selectedChangeTiming;
    }, undefined);
    return matchedTiming ? matchedTiming.featureName : undefined;
}
const pastViewIds = [];
let currentViewId = 'unset';
let onInitialLoadView = true;
// Relevant docs on the data being processed
// https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/?tab=event
// https://docs.datadoghq.com/real_user_monitoring/browser/modifying_data_and_context/?tab=npm
/**
 * Consume the metrics that Datadog RUM is already listening and monitoring for
 * and enrich them with additional relevant data or aggregate them to create custom
 * events composed of metrics from multiple events/locations.
 *
 * @param event
 * @param context
 * @param getCurrentFeatureName
 */
export function aggregateDatadogEvents({ event, context, getCurrentFeatureName = () => 'unknown', }) {
    var _a, _b, _c, _d;
    let enrichedContext = {};
    // avoid sending any url that has hash fragments as they can
    // contain sensitive information
    if (event.view && event.view.url.includes('#')) {
        event.view.url = event.view.url.split('#')[0];
    }
    if ((_b = (_a = event.view) === null || _a === void 0 ? void 0 : _a.referrer) === null || _b === void 0 ? void 0 : _b.includes('#')) {
        event.view.referrer = event.view.referrer.split('#')[0];
    }
    const resource = event.resource;
    if (resource && resource.url && resource.url.includes('#')) {
        resource.url = resource.url.split('#')[0];
    }
    // Handle processing of async networking calls. (including images and other resources)
    if (event.type === 'resource') {
        const requestIsInternal = isUrlInternal(event.resource.url);
        enrichedContext = {
            ...enrichedContext,
            requestIsInternal,
            requestIsSameOrigin: isUrlSameOrigin(event.resource.url),
            requestIsExternalDep: !requestIsInternal && isExternalFunctionalDep(event.resource.url),
            requestApiType: isUrlAServerlessFnCall(event.resource.url)
                ? 'function'
                : 'server',
        };
        if (resource === null || resource === void 0 ? void 0 : resource.url.includes('#access_token')) {
            enrichedContext['resourceHasAccessToken'] = true;
        }
        if (resource === null || resource === void 0 ? void 0 : resource.url.includes('#token')) {
            enrichedContext['resourceHasToken'] = true;
        }
        // expand all performance entry data onto request so we can monitor this in DD
        if ('performanceEntry' in context &&
            context.performanceEntry !== undefined) {
            enrichedContext = {
                ...enrichedContext,
                ...performanceResourceEntryToContextProps(context.performanceEntry),
            };
        }
        if (event.resource.type === 'fetch' || event.resource.type === 'xhr') {
            const resourceContext = context;
            const response = resourceContext === null || resourceContext === void 0 ? void 0 : resourceContext.response;
            const currentFeatureName = getCurrentFeatureName();
            let requestId = null;
            let requestNFServerVersion = null;
            if (response === null || response === void 0 ? void 0 : response.headers) {
                requestId = response.headers.get('x-request-id');
                requestNFServerVersion = response.headers.get('x-nf-srv-version');
            }
            // For feature attribution, async processing can finish after
            // the view that initiated it. In order to apply more accurate
            // attribution, we will attempt to override the feature name and walk
            // back in our view history tracking to get the right view
            if (((_c = resourceContext.performanceEntry) === null || _c === void 0 ? void 0 : _c.startTime) && requestIsInternal) {
                const featureWhereRequestStarted = getViewWhenEventHappened((_d = resourceContext.performanceEntry) === null || _d === void 0 ? void 0 : _d.startTime);
                if (currentFeatureName !== featureWhereRequestStarted) {
                    enrichedContext['featureName'] = featureWhereRequestStarted;
                    enrichedContext['respondedAfterViewChange'] = true;
                }
            }
            enrichedContext = {
                ...enrichedContext,
                requestId,
                requestNFServerVersion,
            };
        }
    }
    else if (event.type === 'view') {
        // A view id is bound to events but the views are relative to navigation.
        // So new ids are a sign of route changes. But internally, DD can still send
        // events relative to a view that happened prior to the most recent navigation.
        // Given that we should make sure the latest view id is not one that has already
        // happened. Note, navigating back to a previous route will still give a new id.
        if (!pastViewIds.includes(event.view.id)) {
            currentViewId = event.view.id;
            pastViewIds.push(event.view.id);
            onInitialLoadView = event.view.loading_type === 'initial_load';
        }
    }
    event.context = {
        ...event.context,
        ...enrichedContext,
    };
}
/**
 * @returns the current datadog view id
 */
export function getDatadogCurrentViewId() {
    return currentViewId;
}
/**
 * Monitor when view changes happen then enrich the current datadog view
 * timing information with these custom metrics.
 */
export function aggregateAppLoadingEvents({ getFeatureConfig }) {
    onMarkAdded((mark) => {
        var _a, _b, _c;
        if (mark.name === MARK_NAME_VIEW_CHANGED) {
            const markNamesToWaitFor = [MARK_NAME_VIEW_INITIAL_RENDER];
            const viewChangedMark = mark;
            const featureConfig = getFeatureConfig((_a = mark.props) === null || _a === void 0 ? void 0 : _a.featureName);
            const featureName = featureConfig.name;
            // some features don't require dfl metrics so we want
            // to avoid waiting for this metric if it's not expected to happen
            if (!((_b = featureConfig === null || featureConfig === void 0 ? void 0 : featureConfig.networkingRules) === null || _b === void 0 ? void 0 : _b.skipMeasuringCriticalPathData)) {
                markNamesToWaitFor.push(MARK_NAME_VIEW_DATA_FULLY_LOADED);
            }
            if ((_c = mark.props) === null || _c === void 0 ? void 0 : _c.featureName) {
                viewChangeTimings.push({
                    featureName,
                    viewStartTime: mark.timeMarked,
                });
            }
            clearMarksMadeListenerByClass(LISTENER_CLASS_FEATURE_LOAD_TIMING);
            whenAllMarksMade({
                listenerClass: LISTENER_CLASS_FEATURE_LOAD_TIMING,
                markNamesToWaitFor,
                startOffset: getAllMarks().length - 1,
                whenMadeCallback: (marks) => {
                    var _a, _b;
                    const virMark = marks[MARK_NAME_VIEW_INITIAL_RENDER];
                    const vdflMark = marks[MARK_NAME_VIEW_DATA_FULLY_LOADED];
                    // Marks are tracked from the window's timing origin (usually doc fetch start).
                    // For non-initial load transitions, need to adjust the time to be based on when the
                    // transition started so that this time is relative to transitions
                    const transitionTime = onInitialLoadView
                        ? 0
                        : viewChangedMark.timeMarked;
                    const vir = virMark.timeMarked - transitionTime;
                    const vdfl = vdflMark ? vdflMark.timeMarked - transitionTime : -1;
                    const irDFLDelta = vdflMark
                        ? vdflMark.timeMarked - virMark.timeMarked
                        : -1;
                    datadogRum.addTiming(DATA_PROP_NAME_VIEW_INITIAL_RENDER, vir);
                    datadogRum.addTiming(DATA_PROP_NAME_VIEW_DATA_FULLY_LOADED, vdfl);
                    datadogRum.addTiming(DATA_PROP_NAME_VIEW_IR_AND_DFL_DELTA, irDFLDelta);
                    if (vdflMark && ((_a = vdflMark.props) === null || _a === void 0 ? void 0 : _a.viewDFLTimedOut) === true) {
                        datadogRum.addAction(CUSTOM_EVENT_DATA_FULLY_LOADED_TIMEOUT, {
                            featureName,
                            viewInitialRender: vir,
                            viewDataFullyLoaded: vdfl,
                            delta: irDFLDelta,
                        });
                    }
                    // @ts-expect-error
                    window._rum.timings.push({
                        featureName,
                        viewInitialRender: vir,
                        viewDataFullyLoaded: vdfl,
                        delta: irDFLDelta,
                        viewDFLTimedOut: !!((_b = vdflMark === null || vdflMark === void 0 ? void 0 : vdflMark.props) === null || _b === void 0 ? void 0 : _b.viewDFLTimedOut),
                    });
                },
                // views can change while this logic
                // is waiting for view timings. This check will ensure the
                // right timings are attributed to the current load
                verifyMark: (mark) => {
                    var _a;
                    return ((_a = mark.props) === null || _a === void 0 ? void 0 : _a.viewId) === getDatadogCurrentViewId();
                },
            });
        }
    });
}
//# sourceMappingURL=index.js.map